/**
 * 「離開頁面」判定的單一事實來源。
 *
 * 為什麼需要：頁面被切到背景（切分頁、切 App、in-app WebView 被收起、iOS 凍結頁面）時，
 * 瀏覽器會 throttle 甚至凍結 timer 與網路，「API 沒慢、只是計時器晚跑」會被記成逾時。
 * 這類事件對「網路 / 後端異常」的診斷沒有價值，需求是：只要使用者在監控窗口內離開過頁面，
 * 該筆逾時就完全不記錄。
 *
 * 設計：模組級單例，第一次 markRequestStart() 時才在 document / window 掛一組 listener，
 * 之後不再移除（一次 page load 只掛一組；測試用 resetPagePresenceForTests() 清除）。
 * 每筆請求只在開始時取一個 token，逾時 / settle 時以 hasLeftPage(token) 查詢。
 *
 * 不使用 beforeunload：行動裝置上不可靠，且掛了 beforeunload 會讓頁面失去 bfcache 資格。
 */

import { nowMs } from 'client/utils/initApiTimeout/clock';

/** 離開頁面的訊號來源 */
const LEFT_REASON = {
    HIDDEN: 'hidden',
    PAGEHIDE: 'pagehide',
    FREEZE: 'freeze'
};

// ---- module scope 狀態（每次 page load 自然重置）----
let installed = false;
let lastLeftAtMs = null; // 最近一次 hidden / pagehide / freeze 的 nowMs()
let lastLeftReason = null; // LEFT_REASON 的成員
let registrations = []; // [{ target, type, handler }]，供 reset 移除

function isDocumentAvailable() {
    return (
        typeof document !== 'undefined' &&
        document != null &&
        typeof document.addEventListener === 'function'
    );
}

function isPageVisibleNow() {
    try {
        return document.visibilityState === 'visible';
    } catch {
        // 讀不到就當作仍在頁面上，寧可多送一筆也不要因例外影響流程
        return true;
    }
}

function markLeft(reason) {
    lastLeftAtMs = nowMs();
    lastLeftReason = reason;
}

function register(target, type, handler) {
    target.addEventListener(type, handler);
    registrations.push({ target, type, handler });
}

/**
 * 懶初始化：掛 listener。任何一步失敗都回 false 並清乾淨，不得影響 API 流程。
 * @returns {boolean} listener 是否已就位
 */
function ensureInstalled() {
    if (installed) return true;
    if (!isDocumentAvailable()) return false;

    try {
        register(document, 'visibilitychange', () => {
            // 只記錄「離開」，不記錄「回來」：回來的時間點對判定沒有意義
            if (!isPageVisibleNow()) markLeft(LEFT_REASON.HIDDEN);
        });
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
            register(window, 'pagehide', () => markLeft(LEFT_REASON.PAGEHIDE));
            // Page Lifecycle API（Chrome 系列）；其他瀏覽器不會觸發，掛著無害
            register(window, 'freeze', () => markLeft(LEFT_REASON.FREEZE));
        }
        installed = true;
        return true;
    } catch {
        resetPagePresenceForTests();
        return false;
    }
}

/**
 * 在請求開始時呼叫，回傳供 hasLeftPage() 使用的 token。
 * @returns {{ available: boolean, startedAtMs: number, startedHidden: boolean }}
 *   available=false 代表沒有 document（SSR / 非 jsdom 測試環境），此時 hasLeftPage 永遠回 false
 */
export function markRequestStart() {
    const startedAtMs = nowMs();
    const available = ensureInstalled();
    if (!available) {
        return { available: false, startedAtMs, startedHidden: false };
    }
    return { available: true, startedAtMs, startedHidden: !isPageVisibleNow() };
}

/**
 * 自 token.startedAtMs 起，是否曾經離開頁面（含當下不可見）。
 *
 * 判定順序：
 *   1. token 不可用 → false（無法判定就不抑制）
 *   2. 請求開始時就不在前景 → true（L1）
 *   3. 當下不在前景 → true（L5，兜底：即使事件遺漏也擋得住）
 *   4. 最近一次離開事件發生在請求開始之後 → true（L2 / L3 / L4）
 *   5. 其餘 → false
 *
 * @param {ReturnType<typeof markRequestStart>} token
 * @returns {boolean}
 */
export function hasLeftPage(token) {
    if (!token || !token.available) return false;
    if (token.startedHidden) return true;
    if (!isPageVisibleNow()) return true;
    return lastLeftAtMs != null && lastLeftAtMs >= token.startedAtMs;
}

/**
 * 供事件 payload 使用的精簡快照：available=false 代表「離開頁面」判定未生效
 * @returns {{ available: boolean }}
 */
export function getPagePresenceSnapshot() {
    return { available: installed };
}

/**
 * 僅供測試：移除 listener 並清空狀態
 */
export function resetPagePresenceForTests() {
    for (const { target, type, handler } of registrations) {
        try {
            target.removeEventListener(type, handler);
        } catch {
            // 移除失敗不影響流程
        }
    }
    registrations = [];
    installed = false;
    lastLeftAtMs = null;
    lastLeftReason = null;
}

/**
 * 僅供測試 / 除錯：最近一次離開頁面的原因
 * @returns {string|null} LEFT_REASON 的成員或 null
 */
export function getLastLeftReason() {
    return lastLeftReason;
}
